import Moment from "moment";
import React, { useMemo, useState } from "react";
import { Button, Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import FormField from "components/FormField";
import { createElementIdFactory } from "utils/elementIds";

const INCOME_BASIS_OPTIONS = [
  { value: "G", label: "Gross" },
  { value: "N", label: "Net" },
];

const percentFormatter = new Intl.NumberFormat("en-AU", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const moneyFormatter = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 2,
  currencyDisplay: "symbol",
});

const incomeCurrFormatter = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 0,
  currencyDisplay: "symbol",
});

function formatMoney(value) {
  const num = parseFloat(String(value).replace(/[^0-9.]/g, ""));
  return isNaN(num) ? value : moneyFormatter.format(num.toFixed(2));
}

function toLocaleDate(date) {
  if (!date) return null;
  return new Date(date).toLocaleDateString("en-AU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * SaleCoreFields
 *
 * Owns all primary sale data fields: dates, price, appendix, classification,
 * income/yield, interest transferred, and transfer number.
 *
 * Income ↔ Yield cross-calculation is fully encapsulated here.
 * The interest_transferred → part_interest link is resolved here and included
 * in the patch sent via onSave so the parent can propagate it to SaleFlagsPanel.
 *
 * Props:
 *   saleId               - read-only display
 *   initialValues        - object with all field initial values
 *   appendixOptions      - loaded by parent, passed as [{value, label, issue_date}]
 *   classificationOptions - loaded by parent
 *   onSave(patch)        - called on blur with changed field values
 */
function SaleCoreFields({
  saleId,
  initialValues,
  appendixOptions,
  classificationOptions,
  onSave,
}) {
  const createId = useMemo(() => createElementIdFactory("sale-detail-core-fields"), []);

  const {
    sale_date: initSaleDate = null,
    contract_date: initContractDate = null,
    transfer_date: initTransferDate = null,
    sale_price: initSalePrice = "",
    appendix: initAppendix = null,
    issue_date: initIssueDate = null,
    classification_code: initClassCode = null,
    classification: initClass = null,
    income: initIncome = "",
    income_basis: initIncomeBasis = null,
    yieldamt: initYield = "",
    income_yield_notes: initYieldNotes = "",
    interest_transferred: initInterest = "",
    transfer_number: initTransferNum = "",
  } = initialValues;

  // --- Date fields ---
  const [saleDate, setSaleDate] = useState(initSaleDate);
  const [contractDate, setContractDate] = useState(initContractDate);
  const [transferDate, setTransferDate] = useState(initTransferDate);

  // --- Money / text fields ---
  const [salePrice, setSalePrice] = useState(initSalePrice ?? "");

  // --- Appendix ---
  const [selectedAppendix, setSelectedAppendix] = useState(
    initAppendix ? { value: initAppendix, label: initAppendix } : null
  );
  const [issueDate, setIssueDate] = useState(initIssueDate);

  // --- Classification ---
  const [selectedClassification, setSelectedClassification] = useState(
    initClassCode ? { value: initClassCode, label: initClass } : null
  );

  // --- Income / Yield ---
  const [income, setIncome] = useState(initIncome ?? "");
  const [selectedIncomeBasis, setSelectedIncomeBasis] = useState(() => {
    if (!initIncomeBasis) return null;
    return INCOME_BASIS_OPTIONS.find((o) => o.value === initIncomeBasis)
      || { value: initIncomeBasis, label: initIncomeBasis };
  });
  const [yieldamt, setYieldamt] = useState(
    initYield != null ? String(initYield) : ""
  );
  const [incomeYieldNotes, setIncomeYieldNotes] = useState(initYieldNotes ?? "");

  // --- Interest / Transfer ---
  const [interestTransferred, setInterestTransferred] = useState(
    initInterest != null ? String(initInterest) : ""
  );
  const [transferNumber, setTransferNumber] = useState(initTransferNum ?? "");

  // ─── Date helpers ────────────────────────────────────────────────────────────

  /**
   * Resolves the effective sale_date: contract date takes priority,
   * then transfer date, then the raw sale date.
   */
  function resolveEffectiveSaleDate(sale, contract, transfer) {
    if (contract) return contract;
    if (transfer) return transfer;
    return sale;
  }

  function handleDateChange(date, fieldName) {
    const val = date ? toLocaleDate(date) : null;

    // Update the correct state slot
    let newSale = saleDate;
    let newContract = contractDate;
    let newTransfer = transferDate;

    if (fieldName === "sale_date") { setSaleDate(val); newSale = val; }
    if (fieldName === "contract_date") { setContractDate(val); newContract = val; }
    if (fieldName === "transfer_date") { setTransferDate(val); newTransfer = val; }

    // Only recompute sale_date when a date is set (preserve original behaviour
    // of not recomputing when a date is cleared)
    const patch = { [fieldName]: val };
    if (date) {
      patch.sale_date = resolveEffectiveSaleDate(newSale, newContract, newTransfer);
    }
    onSave(patch);
  }

  // ─── Sale Price ──────────────────────────────────────────────────────────────

  function handleSalePriceChange(e) {
    setSalePrice(e.target.value !== "" ? e.target.value : "");
  }

  function handleSalePriceBlur() {
    onSave({ sale_price: salePrice || null });
  }

  // ─── Appendix ────────────────────────────────────────────────────────────────

  function handleAppendixChange(option) {
    setSelectedAppendix(option);
    const newIssueDate = option ? option.issue_date : null;
    setIssueDate(newIssueDate);
    onSave({ appendix: option ? option.value : null, issue_date: newIssueDate });
  }

  function handleAssignHistorical() {
    const resolvedDate = resolveEffectiveSaleDate(saleDate, contractDate, transferDate);
    if (!resolvedDate) return;

    let dt = new Date(resolvedDate);
    dt = dt.setDate(dt.getDate() + 30);

    if (dt < new Date(Date.parse("1999-01-01"))) {
      setSelectedAppendix(null);
      setIssueDate(null);
      onSave({ issue_date: null, appendix: null });
      return;
    }

    const sorted = [...appendixOptions].sort((a, b) =>
      a.issue_date < b.issue_date ? -1 : a.issue_date > b.issue_date ? 1 : 0
    );
    for (const opt of sorted) {
      if (new Date(opt.issue_date) > dt) {
        setSelectedAppendix({ value: opt.value, label: opt.label });
        setIssueDate(opt.issue_date);
        onSave({ issue_date: opt.issue_date, appendix: opt.value || "" });
        return;
      }
    }
  }

  // ─── Classification ──────────────────────────────────────────────────────────

  function handleClassificationChange(option) {
    setSelectedClassification(option);
    onSave({ classification_code: option ? option.value : null });
  }

  // ─── Income ──────────────────────────────────────────────────────────────────

  function handleMoneyChange(e) {
    setIncome(e.target.value !== "" ? e.target.value : "");
  }

  async function calculateYield(currentIncome, currentSalePrice) {
    if (!currentSalePrice) { setYieldamt(""); return ""; }

    const priceNum = parseFloat(String(currentSalePrice).replace(/[^0-9.]/g, ""));
    if (isNaN(priceNum)) { setYieldamt(""); return ""; }

    if (currentIncome) {
      const incomeNum = parseFloat(String(currentIncome).replace(/[^0-9.]/g, ""));
      const newYield = ((incomeNum / priceNum) * 100).toFixed(2);
      if (yieldamt) {
        const ok = window.confirm(
          `Is it OK to overwrite the current value of Yield with the new value ${percentFormatter.format(incomeNum / priceNum)}?`
        );
        if (!ok) return yieldamt;
      }
      setYieldamt(newYield);
      return newYield;
    } else {
      if (yieldamt) {
        const ok = window.confirm(
          `Yield cannot be calculated from the current values of Sale Price and Income. ` +
          `Should the current value of Yield ${percentFormatter.format(parseFloat(yieldamt) / 100)} be cleared?`
        );
        if (ok) { setYieldamt(""); return ""; }
      }
    }
    return yieldamt;
  }

  async function handleIncomeBlur() {
    const formatted = income ? formatMoney(income) : "";
    setIncome(formatted);
    const newYield = await calculateYield(formatted, salePrice);
    onSave({
      income: formatted || null,
      yieldamt: newYield !== undefined ? (newYield || null) : (yieldamt || null),
    });
  }

  // ─── Income Basis ─────────────────────────────────────────────────────────────

  function handleIncomeBasisChange(option) {
    setSelectedIncomeBasis(option);
    onSave({ income_basis: option ? option.value : null });
  }

  // ─── Yield ───────────────────────────────────────────────────────────────────

  function handleYieldChange(e) {
    let val = e.target.value
      .replace(/[^0-9.]/g, "")
      .replace(/(?<=[.][0-9]{2})[0-9]*/g, "");
    if (val && parseFloat(val) > 100) val = "100";
    setYieldamt(val);
  }

  async function calculateIncome(currentYield, currentSalePrice) {
    if (!currentSalePrice) { setIncome(""); return ""; }

    const priceNum = parseFloat(String(currentSalePrice).replace(/[^0-9.]/g, ""));
    if (isNaN(priceNum)) { setIncome(""); return ""; }

    if (currentYield) {
      const newIncome = incomeCurrFormatter.format((parseFloat(currentYield) / 100) * priceNum);
      if (income) {
        const ok = window.confirm(
          `Is it OK to overwrite the current value of Income with the new value ${newIncome}?`
        );
        if (!ok) return income;
      }
      setIncome(newIncome);
      return newIncome;
    } else {
      if (income) {
        const rawIncome = parseFloat(String(income).replace(/[^0-9.]/g, ""));
        const ok = window.confirm(
          `Income cannot be calculated from the current values of Sale Price and Yield. ` +
          `Should the current value of Income ${incomeCurrFormatter.format(rawIncome)} be cleared?`
        );
        if (!ok) { setIncome(""); return ""; }
      }
    }
    return income;
  }

  async function handleYieldBlur() {
    const newIncome = await calculateIncome(yieldamt, salePrice);
    onSave({
      yieldamt: yieldamt || null,
      income: newIncome !== undefined ? (newIncome || null) : (income || null),
    });
  }

  function handleIncomeYieldNotesBlur() {
    onSave({ income_yield_notes: incomeYieldNotes || null });
  }

  // ─── Interest Transferred ────────────────────────────────────────────────────

  function handleInterestChange(e) {
    let val = e.target.value
      .replace(/[^0-9.]/g, "")
      .replace(/(?<=[.][0-9]{2})[0-9]*/g, "");
    if (val && parseFloat(val) > 100) val = "100";
    setInterestTransferred(val);
  }

  function handleInterestBlur() {
    const numVal = parseFloat(interestTransferred);
    const partInterest = interestTransferred !== "" && numVal !== 100;
    onSave({
      interest_transferred: interestTransferred || null,
      part_interest: partInterest,
    });
  }

  // ─── Transfer Number ─────────────────────────────────────────────────────────

  function handleTransferNumberBlur() {
    onSave({ transfer_number: transferNumber || null });
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Card id={createId("card")}>
      <Card.Header> </Card.Header>
      <Card.Body className="form-fields-spaced">
        <FormField controlId="sale_id" label="ID">
          <Form.Control id={createId("sale-id")} readOnly value={saleId || ""} as="input" type="text" />
        </FormField>

        <FormField controlId="sale_date" label="Sale Date">
          <DatePicker
            id={createId("sale-date")}
            selected={saleDate ? new Date(saleDate) : null}
            onChange={(d) => handleDateChange(d, "sale_date")}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            isClearable
          />
        </FormField>

        <FormField controlId="contract_date" label="Contract Date">
          <DatePicker
            id={createId("contract-date")}
            selected={contractDate ? new Date(contractDate) : null}
            onChange={(d) => handleDateChange(d, "contract_date")}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            isClearable
          />
        </FormField>

        <FormField controlId="transfer_date" label="Transfer Date">
          <DatePicker
            id={createId("transfer-date")}
            selected={transferDate ? new Date(transferDate) : null}
            onChange={(d) => handleDateChange(d, "transfer_date")}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            isClearable
          />
        </FormField>

        <FormField controlId="sale_price" label="Sale Price">
          <InputGroup>
            <InputGroup.Text>$</InputGroup.Text>
            <Form.Control
              id={createId("sale-price")}
              onChange={handleSalePriceChange}
              value={salePrice}
              as="input"
              type="text"
              onBlur={handleSalePriceBlur}
            />
          </InputGroup>
        </FormField>

        {/* Appendix has an extra action button — can't use FormField wrapper */}
        <Form.Group as={Row} controlId="appendix">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Appendix
          </Form.Label>
          <Col>
            <Select
              inputId={createId("appendix")}
              value={selectedAppendix}
              onChange={handleAppendixChange}
              options={appendixOptions}
            />
          </Col>
          <Col>
            <Button id={createId("assign-historical")} type="button" size="sm" className="btn-teal" onClick={handleAssignHistorical}>
              Assign Historical
            </Button>
          </Col>
        </Form.Group>

        <FormField controlId="issue_date" label="Issue Date">
          <Form.Control
            id={createId("issue-date")}
            value={issueDate ? Moment(issueDate).local().format("DD-MM-YYYY") : ""}
            plaintext
            readOnly
          />
        </FormField>

        <FormField controlId="classification_code" label="Classification">
          <Select
            inputId={createId("classification")}
            value={selectedClassification}
            onChange={handleClassificationChange}
            options={classificationOptions}
            isClearable
          />
        </FormField>

        <FormField controlId="income" label="Income">
          <InputGroup>
            <InputGroup.Text>$</InputGroup.Text>
            <Form.Control
              id={createId("income")}
              onChange={handleMoneyChange}
              value={income}
              as="input"
              type="text"
              onBlur={handleIncomeBlur}
            />
          </InputGroup>
        </FormField>

        <FormField controlId="income_basis" label="Income Basis">
          <Select
            inputId={createId("income-basis")}
            value={selectedIncomeBasis}
            onChange={handleIncomeBasisChange}
            options={INCOME_BASIS_OPTIONS}
            isClearable
          />
        </FormField>

        <FormField controlId="yieldamt" label="Yield">
          <InputGroup>
            <Form.Control
              id={createId("yield")}
              onChange={handleYieldChange}
              value={yieldamt ? yieldamt + "%" : ""}
              as="input"
              type="text"
              onBlur={handleYieldBlur}
              placeholder="0.00 to 100.00"
            />
            <InputGroup.Text>%</InputGroup.Text>
          </InputGroup>
        </FormField>

        <FormField controlId="income_yield_notes" label="Income/Yield Notes">
          <Form.Control
            id={createId("income-yield-notes")}
            onChange={(e) => setIncomeYieldNotes(e.target.value)}
            value={incomeYieldNotes}
            as="textarea"
            rows="2"
            onBlur={handleIncomeYieldNotesBlur}
          />
        </FormField>

        <FormField controlId="interest_transferred" label="Interest Transferred">
          <InputGroup>
            <Form.Control
              id={createId("interest-transferred")}
              onChange={handleInterestChange}
              value={interestTransferred}
              as="input"
              type="text"
              onBlur={handleInterestBlur}
              placeholder="0.00 to 100.00"
            />
            <InputGroup.Text>%</InputGroup.Text>
          </InputGroup>
        </FormField>

        <FormField controlId="transfer_number" label="Transfer No.">
          <Form.Control
            id={createId("transfer-number")}
            onChange={(e) => setTransferNumber(e.target.value)}
            value={transferNumber}
            as="input"
            type="text"
            onBlur={handleTransferNumberBlur}
          />
        </FormField>
      </Card.Body>
    </Card>
  );
}

export default SaleCoreFields;
